/**
 * Centralized error-tracking utility.
 *
 * In development → logs to console.
 * In production  → delegates to Sentry.
 *
 * Set VITE_SENTRY_DSN in your .env to enable Sentry reporting.
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_PRODUCTION = import.meta.env.PROD;
const SENTRY_ENV =
  (import.meta.env.VITE_SENTRY_ENV as string | undefined) ||
  (IS_PRODUCTION ? 'production' : 'development');
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ||
  (import.meta.env.VITE_GIT_SHA as string | undefined) ||
  'unknown';

const SENSITIVE_KEYS = [
  'gstin',
  'pan',
  'tax_id',
  'bank_account',
  'ifsc',
  'iban',
  'account_number',
  'cvv',
  'card_number',
  'card_cvc',
  'password',
  'api_key',
  'token',
  'authorization',
  'cookie',
  'session',
];

const SENSITIVE_KEY_SET = new Set(SENSITIVE_KEYS.map((k) => k.toLowerCase()));

function scrubObject(value: unknown, depth = 0): unknown {
  if (depth > 6 || value == null) return value;
  if (Array.isArray(value)) {
    return value.map((v) => scrubObject(v, depth + 1));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEY_SET.has(k.toLowerCase())) {
        out[k] = '[Filtered]';
      } else {
        out[k] = scrubObject(v, depth + 1);
      }
    }
    return out;
  }
  return value;
}

let sentryInitialized = false;

export function initSentry(): void {
  if (sentryInitialized || !SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    release: APP_VERSION,
    enabled: IS_PRODUCTION,
    sendDefaultPii: false,
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: IS_PRODUCTION ? 1.0 : 0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    ignoreErrors: [
      'ResizeObserver loop',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection',
      /^NetworkError/,
      /extension\//,
      /^chrome-extension:/,
      /^moz-extension:/,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
    beforeSend(event) {
      const url = event.request?.url ?? '';
      if (url.includes('/health') || url.includes('/rest/v1/rpc/health_check')) {
        return null;
      }

      if (event.request?.data) {
        event.request.data = scrubObject(event.request.data) as typeof event.request.data;
      }
      if (event.request?.headers) {
        event.request.headers = scrubObject(event.request.headers) as typeof event.request.headers;
      }
      if (event.request?.query_string && typeof event.request.query_string === 'string') {
        // Mask obvious `token=...` / `api_key=...` patterns in query strings.
        event.request.query_string = event.request.query_string.replace(
          /(token|api_key|authorization|password|cvv)=[^&]+/gi,
          '$1=[Filtered]',
        );
      }
      if (event.extra) {
        event.extra = scrubObject(event.extra) as typeof event.extra;
      }
      if (event.contexts) {
        event.contexts = scrubObject(event.contexts) as typeof event.contexts;
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          data: b.data ? (scrubObject(b.data) as typeof b.data) : b.data,
        }));
      }
      return event;
    },
  });

  sentryInitialized = true;
}

export interface ErrorContext {
  source?: string;
  extra?: Record<string, unknown>;
  userId?: string;
  organizationId?: string;
}

export function captureError(error: unknown, ctx?: ErrorContext): void {
  const normalizedError = error instanceof Error ? error : new Error(String(error));

  console.error(`[ErrorTracker${ctx?.source ? ` | ${ctx.source}` : ''}]`, normalizedError, ctx?.extra);

  if (sentryInitialized) {
    Sentry.withScope((scope) => {
      if (ctx?.source) scope.setTag('source', ctx.source);
      if (ctx?.userId) scope.setUser({ id: ctx.userId });
      if (ctx?.organizationId) scope.setTag('organization_id', ctx.organizationId);
      if (ctx?.extra) scope.setExtras(ctx.extra);
      Sentry.captureException(normalizedError);
    });
  }
}

export function captureMessage(message: string, ctx?: ErrorContext): void {
  console.warn(`[ErrorTracker${ctx?.source ? ` | ${ctx.source}` : ''}]`, message, ctx?.extra);

  if (sentryInitialized) {
    Sentry.captureMessage(message, { level: 'warning', extra: ctx?.extra });
  }
}

export interface TenantContext {
  userId?: string | null;
  organizationId?: string | null;
  plan?: string | null;
  role?: string | null;
}

/**
 * Attach tenant identity to every subsequent Sentry event.
 * Call after login / active-organization resolution.
 */
export function setSentryTenantContext(ctx: TenantContext): void {
  if (!sentryInitialized) return;
  if (ctx.userId) {
    Sentry.setUser({ id: ctx.userId });
  }
  if (ctx.organizationId) Sentry.setTag('tenant_id', ctx.organizationId);
  if (ctx.plan) Sentry.setTag('plan', ctx.plan);
  if (ctx.role) Sentry.setTag('role', ctx.role);
}

/**
 * Clear tenant identity from Sentry scope. Call on sign-out.
 */
export function clearSentryTenantContext(): void {
  if (!sentryInitialized) return;
  Sentry.setUser(null);
  Sentry.setTag('tenant_id', undefined as unknown as string);
  Sentry.setTag('plan', undefined as unknown as string);
  Sentry.setTag('role', undefined as unknown as string);
}

/**
 * Install global handlers for unhandled errors and promise rejections.
 * Call once in main.tsx.
 */
export function installGlobalErrorHandlers(): void {
  initSentry();

  window.addEventListener('error', (event) => {
    captureError(event.error ?? event.message, {
      source: 'window.onerror',
      extra: { filename: event.filename, lineno: event.lineno, colno: event.colno },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason, { source: 'unhandledrejection' });
  });
}
